<template>
    <div class="tactical-board-slide">
        <!-- Header -->
        <div class="slide-header">
            <div class="header-left">
                <i class="bi bi-easel2" style="color: #667eea; font-size: 1.4rem;"></i>
                <h2 v-if="editMode" class="editable-title" contenteditable @blur="onTitleBlur"
                    @keydown.enter.prevent="$event.target.blur()" ref="titleEl">{{ localTitle }}</h2>
                <h2 v-else>{{ localTitle }}</h2>
            </div>
            <div v-if="editMode" class="header-controls">
                <div class="background-selector">
                    <button v-for="bg in backgrounds" :key="bg.id" class="bg-btn" :class="{ active: isBgActive(bg.id) }"
                        :title="bg.label" @click="setBackground(bg.id)">
                        <i :class="bg.icon"></i>
                    </button>
                    <label class="bg-btn upload-btn" title="Upload image background">
                        <i class="bi bi-image"></i>
                        <input type="file" accept="image/*" @change="handleImageUpload" hidden />
                    </label>
                </div>
            </div>
        </div>

        <div class="board-content">
            <!-- Toolbar (edit mode only) -->
            <div v-if="editMode" class="toolbar">
                <!-- Tools -->
                <div class="tool-group">
                    <label class="group-label">Tools</label>
                    <div class="tool-buttons">
                        <button v-for="t in tools" :key="t.id" class="tool-btn" :class="{ active: activeTool === t.id }"
                            :title="t.label" @click="activeTool = t.id">
                            <i :class="t.icon"></i>
                        </button>
                    </div>
                </div>

                <!-- Colors -->
                <div class="tool-group">
                    <label class="group-label">Colour</label>
                    <div class="color-palette">
                        <button v-for="c in palette" :key="c" class="color-swatch"
                            :class="{ active: activeColor === c }" :style="{ backgroundColor: c }"
                            @click="activeColor = c"></button>
                    </div>
                </div>

                <!-- Stroke Width -->
                <div class="tool-group">
                    <label class="group-label">Width</label>
                    <div class="width-options">
                        <button v-for="w in [2, 3, 4, 6]" :key="w" class="width-btn"
                            :class="{ active: activeWidth === w }" @click="activeWidth = w">
                            <span class="width-line" :style="{ height: w + 'px' }"></span>
                        </button>
                    </div>
                </div>

                <!-- Font Size (for text tool) -->
                <div v-if="activeTool === 'text'" class="tool-group">
                    <label class="group-label">Size</label>
                    <select v-model.number="activeFontSize" class="font-select">
                        <option :value="12">S</option>
                        <option :value="16">M</option>
                        <option :value="22">L</option>
                        <option :value="30">XL</option>
                    </select>
                </div>

                <!-- Player Number (for player tool) -->
                <div v-if="activeTool === 'player'" class="tool-group">
                    <label class="group-label">#</label>
                    <input type="number" v-model.number="activePlayerNumber" min="1" max="99"
                        class="player-number-input" />
                </div>

                <!-- Image controls (when background is uploaded image) -->
                <div v-if="localBackground === 'image' && localBackgroundImage" class="tool-group">
                    <label class="group-label">Image</label>
                    <div class="tool-buttons">
                        <button class="tool-btn" title="Zoom in" @click="adjustImageScale(0.1)">
                            <i class="bi bi-zoom-in"></i>
                        </button>
                        <button class="tool-btn" title="Zoom out" @click="adjustImageScale(-0.1)">
                            <i class="bi bi-zoom-out"></i>
                        </button>
                        <button class="tool-btn" title="Fit to canvas" @click="resetImageTransform">
                            <i class="bi bi-arrows-fullscreen"></i>
                        </button>
                    </div>
                </div>

                <!-- Actions -->
                <div class="tool-group actions">
                    <button class="action-btn" @click="undo" title="Undo (Ctrl+Z)" :disabled="elements.length === 0">
                        <i class="bi bi-arrow-counterclockwise"></i>
                    </button>
                    <button class="action-btn danger" @click="clearAll" title="Clear all"
                        :disabled="elements.length === 0">
                        <i class="bi bi-trash3"></i>
                    </button>
                </div>
            </div>

            <!-- Canvas + coaching notes, stacked in a column beside the toolbar -->
            <div class="board-stage">
            <!-- SVG Canvas -->
            <div class="canvas-wrap" ref="canvasWrap">
                <svg ref="svgCanvas" :viewBox="`0 0 ${cW} ${cH}`" class="drawing-canvas"
                    :class="{ editing: editMode, [`cursor-${activeTool}`]: editMode, landscape: cW > cH }"
                    @mousedown="onPointerDown" @mousemove="onPointerMove" @mouseup="onPointerUp"
                    @touchstart.prevent="onPointerDown" @touchmove.prevent="onPointerMove"
                    @touchend.prevent="onPointerUp">
                    <!-- Background -->
                    <rect :width="cW" :height="cH" :fill="pitchBg" />

                    <!-- Uploaded image background (with positioning & sizing) -->
                    <image v-if="localBackground === 'image' && localBackgroundImage" :href="localBackgroundImage"
                        :x="imgX" :y="imgY" :width="imgW" :height="imgH" preserveAspectRatio="xMidYMid slice"
                        class="bg-image" :class="{ draggable: editMode && activeTool === 'select' }"
                        :style="{ pointerEvents: (editMode && activeTool === 'select') ? 'auto' : 'none' }"
                        @mousedown.stop="onBgImageDown" @touchstart.stop.prevent="onBgImageDown" />

                    <!-- SVG file-based pitch background -->
                    <image v-if="currentBgSvg" :href="currentBgSvg" x="0" y="0" :width="cW" :height="cH"
                        preserveAspectRatio="xMidYMid meet" style="pointer-events: none;" />

                    <!-- Full pitch markings (programmatic) -->
                    <g v-if="localBackground === 'full-pitch'" class="pitch-markings">
                        <!-- Outline -->
                        <rect :x="P" :y="P" :width="cW - P * 2" :height="cH - P * 2" fill="none" :stroke="lineC"
                            stroke-width="2" />
                        <!-- Halfway -->
                        <line :x1="P" :y1="cH / 2" :x2="cW - P" :y2="cH / 2" :stroke="lineC" stroke-width="2" />
                        <!-- Center circle -->
                        <circle :cx="cW / 2" :cy="cH / 2" r="45" fill="none" :stroke="lineC" stroke-width="2" />
                        <circle :cx="cW / 2" :cy="cH / 2" r="3" :fill="lineC" />
                        <!-- Top penalty area -->
                        <rect :x="cW / 2 - 80" :y="P" width="160" height="70" fill="none" :stroke="lineC"
                            stroke-width="2" />
                        <rect :x="cW / 2 - 30" :y="P" width="60" height="24" fill="none" :stroke="lineC"
                            stroke-width="2" />
                        <circle :cx="cW / 2" :cy="P + 52" r="3" :fill="lineC" />
                        <!-- Top penalty arc -->
                        <path :d="`M ${cW / 2 - 35} ${P + 70} A 45 45 0 0 0 ${cW / 2 + 35} ${P + 70}`" fill="none"
                            :stroke="lineC" stroke-width="2" />
                        <!-- Bottom penalty area -->
                        <rect :x="cW / 2 - 80" :y="cH - P - 70" width="160" height="70" fill="none" :stroke="lineC"
                            stroke-width="2" />
                        <rect :x="cW / 2 - 30" :y="cH - P - 24" width="60" height="24" fill="none" :stroke="lineC"
                            stroke-width="2" />
                        <circle :cx="cW / 2" :cy="cH - P - 52" r="3" :fill="lineC" />
                        <!-- Bottom penalty arc -->
                        <path :d="`M ${cW / 2 - 35} ${cH - P - 70} A 45 45 0 0 1 ${cW / 2 + 35} ${cH - P - 70}`"
                            fill="none" :stroke="lineC" stroke-width="2" />
                        <!-- Corner arcs -->
                        <path :d="`M ${P} ${P + 10} A 10 10 0 0 0 ${P + 10} ${P}`" fill="none" :stroke="lineC"
                            stroke-width="2" />
                        <path :d="`M ${cW - P - 10} ${P} A 10 10 0 0 0 ${cW - P} ${P + 10}`" fill="none" :stroke="lineC"
                            stroke-width="2" />
                        <path :d="`M ${P} ${cH - P - 10} A 10 10 0 0 1 ${P + 10} ${cH - P}`" fill="none" :stroke="lineC"
                            stroke-width="2" />
                        <path :d="`M ${cW - P - 10} ${cH - P} A 10 10 0 0 1 ${cW - P} ${cH - P - 10}`" fill="none"
                            :stroke="lineC" stroke-width="2" />
                    </g>

                    <!-- Drawn elements -->
                    <g class="elements-layer">
                        <template v-for="(el, idx) in elements">
                            <!-- Arrow -->
                            <g v-if="el.type === 'arrow'" :key="'el-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :transform="elementTransform(el)"
                                @mousedown.stop="onElementDown(idx, $event)">
                                <defs>
                                    <marker :id="'ah-' + idx" markerWidth="10" markerHeight="8" refX="9" refY="4"
                                        orient="auto">
                                        <polygon points="0 0, 10 4, 0 8" :fill="el.color" />
                                    </marker>
                                </defs>
                                <line :x1="el.x1" :y1="el.y1" :x2="el.x2" :y2="el.y2" :stroke="el.color"
                                    :stroke-width="el.strokeWidth" :marker-end="`url(#ah-${idx})`" />
                            </g>

                            <!-- Curved Arrow -->
                            <g v-else-if="el.type === 'curved-arrow'" :key="'curv-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :transform="elementTransform(el)"
                                @mousedown.stop="onElementDown(idx, $event)">
                                <defs>
                                    <marker :id="'cah-' + idx" markerWidth="10" markerHeight="8" refX="9" refY="4"
                                        orient="auto">
                                        <polygon points="0 0, 10 4, 0 8" :fill="el.color" />
                                    </marker>
                                </defs>
                                <path :d="`M ${el.x1} ${el.y1} Q ${el.cpx} ${el.cpy} ${el.x2} ${el.y2}`" fill="none"
                                    :stroke="el.color" :stroke-width="el.strokeWidth"
                                    :marker-end="`url(#cah-${idx})`" />
                            </g>

                            <!-- Solid line -->
                            <line v-else-if="el.type === 'line'" :key="'line-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :x1="el.x1" :y1="el.y1" :x2="el.x2"
                                :y2="el.y2" :stroke="el.color" :stroke-width="el.strokeWidth"
                                @mousedown.stop="onElementDown(idx, $event)" />

                            <!-- Dashed line -->
                            <line v-else-if="el.type === 'dashed'" :key="'dash-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :x1="el.x1" :y1="el.y1" :x2="el.x2"
                                :y2="el.y2" :stroke="el.color" :stroke-width="el.strokeWidth" stroke-dasharray="10 6"
                                @mousedown.stop="onElementDown(idx, $event)" />

                            <!-- Filled circle -->
                            <circle v-else-if="el.type === 'circle-filled'" :key="'cfill-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :cx="el.cx" :cy="el.cy" :r="el.r"
                                :fill="el.color" @mousedown.stop="onElementDown(idx, $event)" />

                            <!-- Empty circle -->
                            <circle v-else-if="el.type === 'circle-empty'" :key="'cemp-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :cx="el.cx" :cy="el.cy" :r="el.r"
                                fill="none" :stroke="el.color" :stroke-width="el.strokeWidth"
                                @mousedown.stop="onElementDown(idx, $event)" />

                            <!-- Zone box (semi-transparent) -->
                            <g v-else-if="el.type === 'zone'" :key="'zone-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :transform="elementTransform(el)"
                                @mousedown.stop="onElementDown(idx, $event)">
                                <rect :x="Math.min(el.x1, el.x2)" :y="Math.min(el.y1, el.y2)"
                                    :width="Math.abs(el.x2 - el.x1)" :height="Math.abs(el.y2 - el.y1)"
                                    :fill="el.color + '33'" :stroke="el.color" :stroke-width="el.strokeWidth"
                                    stroke-dasharray="6 3" rx="4" />
                            </g>

                            <!-- Player marker -->
                            <g v-else-if="el.type === 'player'" :key="'plyr-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :transform="elementTransform(el)"
                                @mousedown.stop="onElementDown(idx, $event)">
                                <circle :cx="el.cx" :cy="el.cy" r="16" :fill="el.color" stroke="#fff"
                                    stroke-width="2" />
                                <text :x="el.cx" :y="el.cy + 5" text-anchor="middle" fill="#fff" font-size="13"
                                    font-weight="700">{{ el.label }}</text>
                            </g>

                            <!-- Text label -->
                            <text v-else-if="el.type === 'text'" :key="'txt-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :x="el.cx" :y="el.cy" :fill="el.color"
                                :font-size="el.fontSize || 16" font-weight="600" dominant-baseline="central"
                                @mousedown.stop="onElementDown(idx, $event)">{{ el.text }}</text>

                            <!-- Goal (top-down box-net, 3 sizes) -->
                            <g v-else-if="el.type === 'goal'" :key="'goal-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :transform="elementTransform(el)"
                                @mousedown.stop="onElementDown(idx, $event)"
                                @touchstart.stop.prevent="onElementDown(idx, $event)">
                                <template v-if="el.goalSize === 'small'">
                                    <rect :x="el.cx - 16" :y="el.cy" width="32" height="18"
                                        fill="rgba(255,255,255,0.06)" />
                                    <line :x1="el.cx - 16" :y1="el.cy + 6" :x2="el.cx + 16" :y2="el.cy + 6"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 16" :y1="el.cy + 12" :x2="el.cx + 16" :y2="el.cy + 12"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 8" :y1="el.cy" :x2="el.cx - 8" :y2="el.cy + 18"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx" :y1="el.cy" :x2="el.cx" :y2="el.cy + 18" :stroke="el.color"
                                        stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx + 8" :y1="el.cy" :x2="el.cx + 8" :y2="el.cy + 18"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 16" :y1="el.cy + 18" :x2="el.cx + 16" :y2="el.cy + 18"
                                        :stroke="el.color" stroke-width="2" opacity="0.6" />
                                    <line :x1="el.cx - 16" :y1="el.cy" :x2="el.cx - 16" :y2="el.cy + 18"
                                        :stroke="el.color" stroke-width="2" />
                                    <line :x1="el.cx + 16" :y1="el.cy" :x2="el.cx + 16" :y2="el.cy + 18"
                                        :stroke="el.color" stroke-width="2" />
                                    <line :x1="el.cx - 16" :y1="el.cy" :x2="el.cx + 16" :y2="el.cy" :stroke="el.color"
                                        stroke-width="4" />
                                    <circle :cx="el.cx - 16" :cy="el.cy" r="3" :fill="el.color" />
                                    <circle :cx="el.cx + 16" :cy="el.cy" r="3" :fill="el.color" />
                                </template>
                                <template v-else-if="el.goalSize === 'medium'">
                                    <rect :x="el.cx - 26" :y="el.cy" width="52" height="22"
                                        fill="rgba(255,255,255,0.06)" />
                                    <line :x1="el.cx - 26" :y1="el.cy + 5.5" :x2="el.cx + 26" :y2="el.cy + 5.5"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 26" :y1="el.cy + 11" :x2="el.cx + 26" :y2="el.cy + 11"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 26" :y1="el.cy + 16.5" :x2="el.cx + 26" :y2="el.cy + 16.5"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 17" :y1="el.cy" :x2="el.cx - 17" :y2="el.cy + 22"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 8.5" :y1="el.cy" :x2="el.cx - 8.5" :y2="el.cy + 22"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx" :y1="el.cy" :x2="el.cx" :y2="el.cy + 22" :stroke="el.color"
                                        stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx + 8.5" :y1="el.cy" :x2="el.cx + 8.5" :y2="el.cy + 22"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx + 17" :y1="el.cy" :x2="el.cx + 17" :y2="el.cy + 22"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 26" :y1="el.cy + 22" :x2="el.cx + 26" :y2="el.cy + 22"
                                        :stroke="el.color" stroke-width="2" opacity="0.6" />
                                    <line :x1="el.cx - 26" :y1="el.cy" :x2="el.cx - 26" :y2="el.cy + 22"
                                        :stroke="el.color" stroke-width="2" />
                                    <line :x1="el.cx + 26" :y1="el.cy" :x2="el.cx + 26" :y2="el.cy + 22"
                                        :stroke="el.color" stroke-width="2" />
                                    <line :x1="el.cx - 26" :y1="el.cy" :x2="el.cx + 26" :y2="el.cy" :stroke="el.color"
                                        stroke-width="4" />
                                    <circle :cx="el.cx - 26" :cy="el.cy" r="3" :fill="el.color" />
                                    <circle :cx="el.cx + 26" :cy="el.cy" r="3" :fill="el.color" />
                                </template>
                                <template v-else>
                                    <rect :x="el.cx - 40" :y="el.cy" width="80" height="28"
                                        fill="rgba(255,255,255,0.06)" />
                                    <line :x1="el.cx - 40" :y1="el.cy + 7" :x2="el.cx + 40" :y2="el.cy + 7"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 40" :y1="el.cy + 14" :x2="el.cx + 40" :y2="el.cy + 14"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 40" :y1="el.cy + 21" :x2="el.cx + 40" :y2="el.cy + 21"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 30" :y1="el.cy" :x2="el.cx - 30" :y2="el.cy + 28"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 20" :y1="el.cy" :x2="el.cx - 20" :y2="el.cy + 28"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 10" :y1="el.cy" :x2="el.cx - 10" :y2="el.cy + 28"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx" :y1="el.cy" :x2="el.cx" :y2="el.cy + 28" :stroke="el.color"
                                        stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx + 10" :y1="el.cy" :x2="el.cx + 10" :y2="el.cy + 28"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx + 20" :y1="el.cy" :x2="el.cx + 20" :y2="el.cy + 28"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx + 30" :y1="el.cy" :x2="el.cx + 30" :y2="el.cy + 28"
                                        :stroke="el.color" stroke-width="0.5" opacity="0.25" />
                                    <line :x1="el.cx - 40" :y1="el.cy + 28" :x2="el.cx + 40" :y2="el.cy + 28"
                                        :stroke="el.color" stroke-width="2" opacity="0.6" />
                                    <line :x1="el.cx - 40" :y1="el.cy" :x2="el.cx - 40" :y2="el.cy + 28"
                                        :stroke="el.color" stroke-width="2.5" />
                                    <line :x1="el.cx + 40" :y1="el.cy" :x2="el.cx + 40" :y2="el.cy + 28"
                                        :stroke="el.color" stroke-width="2.5" />
                                    <line :x1="el.cx - 40" :y1="el.cy" :x2="el.cx + 40" :y2="el.cy" :stroke="el.color"
                                        stroke-width="5" />
                                    <circle :cx="el.cx - 40" :cy="el.cy" r="4" :fill="el.color" />
                                    <circle :cx="el.cx + 40" :cy="el.cy" r="4" :fill="el.color" />
                                </template>
                            </g>

                            <!-- Ball -->
                            <g v-else-if="el.type === 'ball'" :key="'ball-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :transform="elementTransform(el)"
                                @mousedown.stop="onElementDown(idx, $event)">
                                <g :transform="`translate(${el.cx - 10}, ${el.cy - 10}) scale(0.0787)`">
                                    <path
                                        d="M213.941149,35.1829989 C191.829259,14.1838689 162.147229,1.11174885 129.402299,0.471068853 L136.809459,31.1084989 L174.539329,46.5719289 L213.941149,35.1829989 Z"
                                        fill="#EEEEEE" />
                                    <polygon fill="#EEEEEE"
                                        points="200.225539 172.181739 212.420939 131.380539 176.087639 98.9250389 133.909539 111.457639 119.971939 152.792739 143.632139 179.824939" />
                                    <polygon fill="#EEEEEE"
                                        points="125.816739 108.760039 100.807739 75.2367389 47.6144389 86.2800389 38.6505389 118.173539 73.9160389 158.946639 111.851039 150.207539" />
                                    <path
                                        d="M220.724489,42.0955989 L180.884309,53.6081689 L180.884309,91.7820189 L219.681979,126.432129 L253.244619,123.903129 C252.519639,92.4592289 240.383249,63.8365689 220.724489,42.0955989 Z"
                                        fill="#EEEEEE" />
                                    <path
                                        d="M120.660389,0.594708853 C81.7924689,2.48302885 47.5273289,21.8804589 25.6261889,51.1072689 L46.0380289,77.9006189 L99.4111689,66.8067389 L128.354169,32.4151489 L120.660389,0.594708853 Z"
                                        fill="#EEEEEE" />
                                    <path
                                        d="M121.697279,249.268469 L121.697279,248.891929 L116.830359,220.109099 L73.5113989,207.214009 L46.6028389,224.447739 C67.3799789,241.566259 93.6506689,252.235829 122.430689,253.233379 L121.759099,249.268469 L121.697279,249.268469 Z"
                                        fill="#B8B8B8" />
                                    <path
                                        d="M69.2064789,199.848999 L69.2064789,166.558929 L32.0891889,123.616509 L0.420488853,126.876109 C0.420488853,163.007089 15.5972989,195.574989 39.8925589,218.619799 L69.2064789,199.848999 Z"
                                        fill="#F1F1F1" />
                                    <path
                                        d="M145.666579,188.167829 L125.100189,217.875149 L131.079869,253.233379 C165.704689,252.103759 196.800149,237.078689 218.965429,213.519649 L200.756629,180.698849 L145.666579,188.167829 Z"
                                        fill="#EEEEEE" />
                                    <path
                                        d="M122.298619,4.97830885 C122.144069,5.13285885 121.986709,5.28740885 121.832159,5.44195885 L120.660389,0.591898853 C81.7924689,2.48302885 47.5273289,21.8804589 25.6261889,51.1072689 L46.0380289,77.9006189 L74.8377189,71.9153189 C84.4366789,49.3847389 100.285079,27.0817689 122.301429,5.00640885 L122.301429,4.97830885 L122.298619,4.97830885 Z"
                                        fill="#B8B8B8" />
                                    <path
                                        d="M47.6003889,86.2828489 L38.6449189,118.162299 L66.2615989,150.111999 C61.6363389,126.971649 63.2914289,104.061719 71.1847189,81.3793989 L47.6003889,86.2828489 Z"
                                        fill="#B8B8B8" />
                                    <path
                                        d="M121.697279,249.268469 L121.697279,248.891929 L121.472479,247.554369 C121.545539,247.635859 121.624219,247.714539 121.697279,247.796029 L121.697279,247.706109 C111.047379,236.193539 101.757519,224.728739 94.0468789,213.322949 C94.0468789,213.322949 94.0468789,213.325759 94.0496889,213.325759 L73.5113989,207.211199 L46.6028389,224.447739 C67.3799789,241.566259 93.6506689,252.235829 122.430689,253.233379 L121.759099,249.268469 L121.697279,249.268469 Z"
                                        fill="#B8B8B8" />
                                    <path
                                        d="M69.2064789,199.848999 L69.2064789,166.558929 L32.0891889,123.616509 L0.420488853,126.876109 C0.420488853,163.007089 15.5972989,195.574989 39.8925589,218.619799 L69.2064789,199.848999 Z"
                                        fill="#B8B8B8" />
                                    <path
                                        d="M69.2064789,166.558929 L69.2064789,199.848999 L39.8953689,218.622609 C42.0365889,220.654239 44.2255789,222.635289 46.5044889,224.512369 L73.5142089,207.214009 L116.833169,220.109099 L122.433499,253.233379 C123.917179,253.283959 125.395239,253.345779 126.892969,253.345779 C128.297969,253.345779 129.688919,253.283959 131.082679,253.238999 L125.102999,217.875149 L145.669389,188.167829 L200.756629,180.698849 L218.968239,213.525269 C219.159319,213.322949 219.356019,213.123439 219.547099,212.918309 C220.187779,212.238289 220.803169,211.544219 221.424179,210.852959 C230.393699,200.762249 237.781189,189.238439 243.176389,176.649639 C243.420859,176.062349 243.668139,175.477869 243.904179,174.884959 C244.216089,174.123449 244.511139,173.353509 244.811809,172.586379 C250.212629,158.235709 253.045109,142.477229 253.323299,125.319369 C253.317679,124.844479 253.298009,124.375209 253.286769,123.900319 L219.687599,126.429319 L180.887119,91.7820189 L180.887119,53.6081689 L220.727299,42.0955989 C218.560789,39.6986689 216.284689,37.4057089 213.941149,35.1829989 L174.536519,46.5691189 L136.806649,31.1056889 L129.402299,0.471068853 C128.562109,0.454208853 127.733159,0.406438853 126.890159,0.406438853 C124.796709,0.406438853 122.720119,0.462638853 120.651959,0.563798853 L128.351359,32.4151489 L99.4083589,66.8067389 L46.0352189,77.9006189 L25.6233789,51.1072689 C25.6149489,51.1185089 25.6093289,51.1269389 25.6008989,51.1353689 C17.4153689,62.3641289 11.3513889,73.9919089 7.21506885,85.9877989 C6.93687885,86.8026989 6.65025885,87.6091689 6.38892885,88.4324989 C6.16131885,89.1378089 5.95337885,89.8487389 5.73981885,90.5568589 C5.35484885,91.8410289 4.98392885,93.1308189 4.63829885,94.4318489 C4.50903885,94.9151689 4.37977885,95.4012989 4.25894885,95.8846189 C3.83182885,97.5846689 3.43280885,99.2959589 3.07312885,101.021299 C3.03940885,101.181469 3.00006885,101.338829 2.96915885,101.498999 C2.56451885,103.482859 2.20764885,105.483579 1.89854885,107.501159 C1.86763885,107.711909 1.84234885,107.922659 1.81143885,108.133409 C1.56696885,109.785689 1.35902885,111.452019 1.17637885,113.126779 C1.10612885,113.795559 1.04430885,114.464339 0.982488853,115.133119 C0.867278853,116.394809 0.771738853,117.662119 0.693058853,118.932239 C0.648098853,119.668459 0.597518853,120.404679 0.563798853,121.143709 C0.479498853,123.046079 0.417678853,124.954069 0.417678853,126.876109 L32.0863789,123.616509 L69.2064789,166.558929 Z M133.903919,111.446399 L176.084829,98.9250389 L212.432179,131.386159 L200.231159,172.167689 L143.640569,179.838989 L119.969129,152.784309 L133.903919,111.446399 Z M47.6003889,86.2828489 L100.802119,75.2254989 L125.805499,108.774089 L111.842609,150.201919 L73.9019889,158.957879 L38.6449189,118.162299 L47.6003889,86.2828489 Z"
                                        fill="#535353" />
                                    <path
                                        d="M121.832159,5.44195885 C123.501299,3.76157885 125.150769,2.08119885 126.890159,0.406438853 C124.796709,0.406438853 122.720119,0.462638853 120.651959,0.563798853 L121.832159,5.44195885 Z"
                                        fill="#3F4042" />
                                    <path
                                        d="M69.2064789,166.558929 L69.2064789,199.848999 L39.8953689,218.622609 C42.0365889,220.654239 44.2255789,222.635289 46.5044889,224.512369 L73.5142089,207.214009 L94.0524989,213.328569 C79.6849689,192.070919 70.4344489,171.001539 66.2615989,150.117619 L38.6449189,118.167919 L47.6003889,86.2884689 L71.1819089,81.3878289 C72.2806189,78.2265789 73.4917289,75.0709489 74.8349089,71.9209389 L46.0352189,77.9062389 L25.6233789,51.1128889 C25.6149489,51.1241289 25.6065189,51.1325589 25.6008989,51.1409889 C17.4153689,62.3697489 11.3541989,73.9975289 7.21506885,85.9906089 C7.08018885,86.3840089 6.94530885,86.7745989 6.81604885,87.1708089 C6.67273885,87.5923089 6.52380885,88.0109989 6.38892885,88.4381189 C6.16131885,89.1434289 5.95337885,89.8543589 5.73981885,90.5624789 C5.36327885,91.8185489 5.00078885,93.0802389 4.66358885,94.3531689 C4.65515885,94.3812689 4.64672885,94.4093689 4.63829885,94.4374689 C4.50903885,94.9207889 4.37977885,95.4041089 4.25894885,95.8902389 C3.83182885,97.5902889 3.42999885,99.3043889 3.07312885,101.032539 C3.03940885,101.192709 3.00006885,101.350069 2.96915885,101.507429 C2.56451885,103.491289 2.20764885,105.492009 1.89854885,107.509589 C1.86763885,107.717529 1.84515885,107.925469 1.81424885,108.133409 C1.58101885,109.695769 1.38993885,111.272179 1.21571885,112.851399 C1.20447885,112.946939 1.19042885,113.039669 1.17918885,113.132399 C1.10893885,113.801179 1.04711885,114.469959 0.985298853,115.138739 C0.881328853,116.254309 0.802648853,117.378309 0.729588853,118.505119 C0.721158853,118.651239 0.704298853,118.794549 0.695868853,118.940669 C0.650908853,119.676889 0.600328853,120.413109 0.566608853,121.152139 C0.482308853,123.054509 0.420488853,124.962499 0.420488853,126.884539 L32.0891889,123.624939 L69.2064789,166.558929 Z"
                                        fill="#070707" />
                                    <path
                                        d="M122.430689,253.233379 C123.914369,253.283959 125.392429,253.345779 126.890159,253.345779 C125.027129,251.412499 123.254019,249.484839 121.472479,247.554369 L122.430689,253.233379 Z"
                                        fill="#3F4042" />
                                </g>
                            </g>

                            <!-- Cone / Marker -->
                            <g v-else-if="el.type === 'cone'" :key="'cone-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :transform="elementTransform(el)"
                                @mousedown.stop="onElementDown(idx, $event)">
                                <polygon
                                    :points="`${el.cx},${el.cy - 12} ${el.cx - 9},${el.cy + 6} ${el.cx + 9},${el.cy + 6}`"
                                    :fill="el.color" :stroke="el.color" stroke-width="1" opacity="0.85" />
                                <line :x1="el.cx - 10" :y1="el.cy + 6" :x2="el.cx + 10" :y2="el.cy + 6"
                                    :stroke="el.color" stroke-width="2.5" stroke-linecap="round" />
                            </g>

                            <!-- Ladder -->
                            <g v-else-if="el.type === 'ladder'" :key="'ladder-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :transform="elementTransform(el)"
                                @mousedown.stop="onElementDown(idx, $event)">
                                <g :transform="`translate(${el.cx - 7}, ${el.cy - 24}) scale(0.1)`">
                                    <rect fill="#C7825A" x="0" y="0" width="11" height="663" />
                                    <rect fill="#C7825A" x="134" y="0" width="11" height="663" />
                                    <rect fill="#6C4A36" x="0" y="6" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="49" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="92" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="135" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="178" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="221" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="264" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="307" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="350" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="393" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="436" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="479" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="525" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="568" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="611" width="139.5" height="9" />
                                    <rect fill="#6C4A36" x="0" y="654" width="139.5" height="9" />
                                </g>
                            </g>

                            <!-- Manikin -->
                            <g v-else-if="el.type === 'manikin'" :key="'manikin-' + idx" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :transform="elementTransform(el)"
                                @mousedown.stop="onElementDown(idx, $event)">
                                <g :transform="`translate(${el.cx - 8}, ${el.cy - 22}) scale(0.068)`">
                                    <circle stroke="#E9D983" stroke-width="10" cx="117.714844" cy="53.7148438"
                                        r="48.7148438" />
                                    <rect stroke="#E9D983" stroke-width="10" fill-opacity="0.69" fill="#E9DD8C"
                                        x="11.2148438" y="109" width="213" height="220" rx="24" />
                                    <rect fill="#E9D983" x="44" y="173" width="148" height="53" rx="8" />
                                    <rect stroke="#E9D983" stroke-width="4" x="85.2148438" y="106" width="65"
                                        height="528" />
                                    <path
                                        d="M112.347656,654.109375 L6.34765625,654.109375 L6.34765625,635.570312 L61.0664062,635.570312 L41.01725,524.916374 L6.34765625,333.570312 L83.3476562,333.570312 L83.3476562,635.570312 L112.347656,635.570312 L152.214844,635.570312 L152.214844,333.570312 L229.214844,333.570312 L194.54525,524.916374 L174.496094,635.570312 L229.214844,635.570312 L229.214844,654.109375 L112.347656,654.109375 Z"
                                        stroke="#E9D983" stroke-width="10" />
                                </g>
                            </g>

                            <!-- Hurdle -->
                            <g v-else-if="el.type === 'hurdle'" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :transform="elementTransform(el)"
                                @mousedown.stop="onElementDown(idx, $event)"
                                @touchstart.stop.prevent="onElementDown(idx, $event)">
                                <line :x1="el.cx - 12" :y1="el.cy - 6" :x2="el.cx + 12" :y2="el.cy - 6"
                                    :stroke="el.color" stroke-width="2.5" />
                                <line :x1="el.cx - 10" :y1="el.cy - 6" :x2="el.cx - 10" :y2="el.cy + 6"
                                    :stroke="el.color" stroke-width="2" />
                                <line :x1="el.cx + 10" :y1="el.cy - 6" :x2="el.cx + 10" :y2="el.cy + 6"
                                    :stroke="el.color" stroke-width="2" />
                            </g>

                            <!-- Flat Disc -->
                            <g v-else-if="el.type === 'disk'" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :transform="elementTransform(el)"
                                @mousedown.stop="onElementDown(idx, $event)"
                                @touchstart.stop.prevent="onElementDown(idx, $event)">
                                <svg :x="el.cx - 10" :y="el.cy - 5" width="20" height="9" viewBox="0 0 357 155">
                                    <g fill-rule="nonzero">
                                        <ellipse :fill="el.color" cx="178.5" cy="77.5" rx="178.5" ry="77.5" />
                                        <ellipse fill="#393535" cx="178.5" cy="41.5" rx="63" ry="21.5" />
                                    </g>
                                </svg>
                            </g>
                            <!-- Freehand path -->
                            <path v-else-if="el.type === 'freehand'" class="drawn-element"
                                :class="{ selected: selectedIndex === idx }" :d="el.d" fill="none" :stroke="el.color"
                                :stroke-width="el.strokeWidth" stroke-linecap="round" stroke-linejoin="round"
                                @mousedown.stop="onElementDown(idx, $event)"
                                @touchstart.stop.prevent="onElementDown(idx, $event)" />
                        </template>
                    </g>

                    <!-- Freehand preview -->
                    <path v-if="drawing && activeTool === 'freehand' && freehandPreviewPath" :d="freehandPreviewPath"
                        fill="none" :stroke="activeColor" :stroke-width="activeWidth" stroke-linecap="round"
                        stroke-linejoin="round" opacity="0.6" />

                    <!-- Live preview while drawing -->
                    <g v-if="drawing && previewEl" class="preview-layer" opacity="0.6">
                        <line v-if="previewEl.type === 'arrow'" :x1="previewEl.x1" :y1="previewEl.y1" :x2="previewEl.x2"
                            :y2="previewEl.y2" :stroke="previewEl.color" :stroke-width="previewEl.strokeWidth" />
                        <path v-else-if="previewEl.type === 'curved-arrow'"
                            :d="`M ${previewEl.x1} ${previewEl.y1} Q ${previewEl.cpx} ${previewEl.cpy} ${previewEl.x2} ${previewEl.y2}`"
                            fill="none" :stroke="previewEl.color" :stroke-width="previewEl.strokeWidth" />
                        <line v-else-if="previewEl.type === 'line'" :x1="previewEl.x1" :y1="previewEl.y1"
                            :x2="previewEl.x2" :y2="previewEl.y2" :stroke="previewEl.color"
                            :stroke-width="previewEl.strokeWidth" />
                        <line v-else-if="previewEl.type === 'dashed'" :x1="previewEl.x1" :y1="previewEl.y1"
                            :x2="previewEl.x2" :y2="previewEl.y2" :stroke="previewEl.color"
                            :stroke-width="previewEl.strokeWidth" stroke-dasharray="10 6" />
                        <circle v-else-if="previewEl.type === 'circle-filled'" :cx="previewEl.cx" :cy="previewEl.cy"
                            :r="previewEl.r" :fill="previewEl.color" />
                        <circle v-else-if="previewEl.type === 'circle-empty'" :cx="previewEl.cx" :cy="previewEl.cy"
                            :r="previewEl.r" fill="none" :stroke="previewEl.color"
                            :stroke-width="previewEl.strokeWidth" />
                        <rect v-else-if="previewEl.type === 'zone'" :x="Math.min(previewEl.x1, previewEl.x2)"
                            :y="Math.min(previewEl.y1, previewEl.y2)" :width="Math.abs(previewEl.x2 - previewEl.x1)"
                            :height="Math.abs(previewEl.y2 - previewEl.y1)" :fill="previewEl.color + '33'"
                            :stroke="previewEl.color" :stroke-width="previewEl.strokeWidth" stroke-dasharray="6 3"
                            rx="4" />
                    </g>

                    <!-- Rotation handle -->
                    <g v-if="editMode && rotateHandlePos" class="rotation-handle">
                        <line :x1="rotateHandlePos.lx" :y1="rotateHandlePos.ly" :x2="rotateHandlePos.x"
                            :y2="rotateHandlePos.y" stroke="#667eea" stroke-width="1" stroke-dasharray="3 2"
                            opacity="0.7" />
                        <circle :cx="rotateHandlePos.x" :cy="rotateHandlePos.y" r="7" fill="#667eea" stroke="#fff"
                            stroke-width="1.5" style="cursor: grab" @mousedown.stop="onRotateStart($event)"
                            @touchstart.stop.prevent="onRotateStart($event)" />
                        <text :x="rotateHandlePos.x" :y="rotateHandlePos.y + 1" text-anchor="middle"
                            dominant-baseline="central" fill="#fff" font-size="9"
                            style="pointer-events: none">&#8635;</text>
                    </g>

                    <!-- Scale handle -->
                    <g v-if="editMode && scaleHandlePos" class="scale-handle">
                        <line :x1="scaleHandlePos.lx" :y1="scaleHandlePos.ly" :x2="scaleHandlePos.x"
                            :y2="scaleHandlePos.y" stroke="#22c55e" stroke-width="1" stroke-dasharray="3 2"
                            opacity="0.7" />
                        <circle :cx="scaleHandlePos.x" :cy="scaleHandlePos.y" r="7" fill="#22c55e" stroke="#fff"
                            stroke-width="1.5" style="cursor: nwse-resize" @mousedown.stop="onScaleStart($event)"
                            @touchstart.stop.prevent="onScaleStart($event)" />
                        <text :x="scaleHandlePos.x" :y="scaleHandlePos.y + 1" text-anchor="middle"
                            dominant-baseline="central" fill="#fff" font-size="9"
                            style="pointer-events: none">&#x21D5;</text>
                    </g>
                </svg>
            </div>

            <!-- Notes panel -->
            <div class="notes-panel">
                <h4><i class="bi bi-journal-text"></i> Coaching Notes</h4>
                <div v-if="editMode" class="notes-editor-wrapper">
                    <QuillEditor v-model:content="localNotes" contentType="html" theme="snow"
                        :toolbar="notesToolbarOptions" placeholder="Add coaching notes, instructions, or key points..."
                        @update:content="emitNotes" />
                </div>
                <div v-else class="notes-display">
                    <div v-if="localNotes && localNotes !== '<p><br></p>'" v-html="localNotes" class="notes-rendered">
                    </div>
                    <p v-else class="placeholder">No notes added</p>
                </div>
            </div>
            </div>
        </div>
    </div>
</template>

<script>
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

export default {
    name: 'TacticalBoardCanvas',
    components: { QuillEditor },
    props: {
        title: { type: String, default: 'Tactical Board' },
        notes: { type: String, default: '' },
        savedElements: { type: Array, default: () => [] },
        background: { type: String, default: 'full-pitch' },
        savedBackgroundImage: { type: String, default: '' },
        orientation: { type: String, default: 'portrait' },
        imageTransform: { type: Object, default: () => ({ x: 0, y: 0, scale: 1 }) },
        editMode: { type: Boolean, default: false },
    },
    emits: [
        'update:elements',
        'update:title',
        'update:notes',
        'update:background',
        'update:backgroundImage',
        'update:orientation',
        'update:imageTransform',
    ],
    data() {
        return {
            // Canvas base dimensions
            W: 500,
            H: 700,
            P: 20, // pitch padding

            // Drawing state
            elements: [],
            selectedIndex: -1,
            drawing: false,
            dragging: false,
            rotating: false,
            resizing: false,
            scaleStartDist: 0,
            scaleStartVal: 1,
            dragOffset: { dx: 0, dy: 0 },
            previewEl: null,
            drawStart: null,
            freehandPoints: [],

            // Background image drag state
            draggingImage: false,
            imgDragStart: null,

            // Quill toolbar options for notes
            notesToolbarOptions: [
                ['bold', 'italic', 'underline'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'header': [3, 4, false] }],
                [{ 'color': [] }],
                ['clean'],
            ],

            // Local copies
            localTitle: this.title,
            localNotes: this.notes,
            localBackground: this.background,
            localBackgroundImage: this.savedBackgroundImage,
            localOrientation: this.orientation || 'portrait',
            localImageTransform: { x: 0, y: 0, scale: 1, ...(this.imageTransform || {}) },

            // Active tool state
            activeTool: 'arrow',
            activeColor: '#ffffff',
            activeWidth: 3,
            activeFontSize: 16,
            activePlayerNumber: 9,

            // Tool definitions
            tools: [
                { id: 'select', label: 'Select / Move', icon: 'bi bi-cursor' },
                { id: 'arrow', label: 'Arrow', icon: 'bi bi-arrow-up-right' },
                { id: 'curved-arrow', label: 'Curved Arrow Left', icon: 'bi bi-bezier2' },
                { id: 'curved-arrow-right', label: 'Curved Arrow Right', icon: 'bi bi-bezier' },
                { id: 'line', label: 'Solid Line', icon: 'bi bi-dash-lg' },
                { id: 'dashed', label: 'Dashed Line', icon: 'bi bi-three-dots' },
                { id: 'circle-filled', label: 'Filled Circle', icon: 'bi bi-circle-fill' },
                { id: 'circle-empty', label: 'Empty Circle', icon: 'bi bi-circle' },
                { id: 'zone', label: 'Zone Box', icon: 'bi bi-bounding-box' },
                { id: 'player', label: 'Player Marker', icon: 'bi bi-person-fill' },
                { id: 'text', label: 'Text Label', icon: 'bi bi-fonts' },
                { id: 'goal-small', label: 'Goal (Small)', icon: 'bi bi-border-bottom' },
                { id: 'goal-medium', label: 'Goal (Medium)', icon: 'bi bi-border-bottom' },
                { id: 'goal-large', label: 'Goal (Large)', icon: 'bi bi-border-bottom' },
                { id: 'ball', label: 'Ball', icon: 'bi bi-record-circle' },
                { id: 'cone', label: 'Cone / Marker', icon: 'bi bi-triangle' },
                { id: 'ladder', label: 'Agility Ladder', icon: 'bi bi-ladder' },
                { id: 'manikin', label: 'Manikin', icon: 'bi bi-person-standing' },
                { id: 'hurdle', label: 'Hurdle', icon: 'bi bi-distribute-vertical' },
                { id: 'disk', label: 'Flat Disc', icon: 'bi bi-record-btn' },
                { id: 'freehand', label: 'Freehand Draw', icon: 'bi bi-pencil' },
            ],

            // Color palette
            palette: [
                '#ffffff', '#f43f5e', '#3b82f6', '#22c55e',
                '#eab308', '#f97316', '#a855f7', '#06b6d4',
            ],

            // Background options
            backgrounds: [
                { id: 'full-pitch', label: 'Full Pitch', icon: 'bi bi-grid-3x3', w: 500, h: 700 },
                { id: 'full-pitch-landscape', label: 'Full Pitch (Landscape)', icon: 'bi bi-tablet-landscape', w: 700, h: 500, svgFile: '/assets/pitch/full-pitch-landscape.svg' },
                { id: 'half-pitch', label: 'Half Pitch', icon: 'bi bi-layout-split', w: 396, h: 500, svgFile: '/assets/pitch/half-pitch.svg' },
                { id: 'pitch-two', label: 'Pitch (2 Zones)', icon: 'bi bi-layout-split', w: 700, h: 500, svgFile: '/assets/pitch/pitch-two.svg' },
                { id: 'pitch-three', label: 'Pitch (3 Zones)', icon: 'bi bi-layout-three-columns', w: 700, h: 500, svgFile: '/assets/pitch/pitch-three.svg' },
                { id: 'blank', label: 'Blank', icon: 'bi bi-square', w: 500, h: 700 },
            ],
        }
    },
    computed: {
        cW() {
            const bg = this.backgrounds.find(b => b.id === this.localBackground)
            if (bg?.svgFile) return bg.w
            return this.localOrientation === 'landscape' ? this.H : this.W
        },
        cH() {
            const bg = this.backgrounds.find(b => b.id === this.localBackground)
            if (bg?.svgFile) return bg.h
            return this.localOrientation === 'landscape' ? this.W : this.H
        },
        currentBgSvg() {
            const bg = this.backgrounds.find(b => b.id === this.localBackground)
            return bg?.svgFile || null
        },
        freehandPreviewPath() {
            if (this.freehandPoints.length < 2) return ''
            const pts = this.freehandPoints
            let d = `M ${pts[0].x} ${pts[0].y}`
            for (let i = 1; i < pts.length; i++) {
                d += ` L ${pts[i].x} ${pts[i].y}`
            }
            return d
        },
        isRotatable() {
            if (this.selectedIndex < 0) return false
            const el = this.elements[this.selectedIndex]
            return el && ['arrow', 'curved-arrow', 'zone', 'goal', 'cone', 'ball', 'ladder', 'manikin', 'hurdle', 'disk', 'player', 'text'].includes(el.type)
        },
        isScalable() {
            if (this.selectedIndex < 0) return false
            const el = this.elements[this.selectedIndex]
            return el && ['player', 'goal', 'ball', 'cone', 'ladder', 'manikin', 'hurdle', 'disk'].includes(el.type)
        },
        rotateHandlePos() {
            if (this.selectedIndex < 0) return null
            const el = this.elements[this.selectedIndex]
            if (!el || !this.isRotatable) return null
            if (this.activeTool !== 'select') return null
            const dist = 32
            const rad = (el.rotation || 0) * Math.PI / 180
            let cx, cy
            if (el.cx !== undefined) {
                cx = el.cx
                cy = el.cy
            } else if (el.x1 !== undefined) {
                cx = (el.x1 + el.x2) / 2
                cy = (el.y1 + el.y2) / 2
            } else {
                return null
            }
            return {
                lx: cx, ly: cy,
                x: cx + dist * Math.sin(rad),
                y: cy - dist * Math.cos(rad),
            }
        },
        scaleHandlePos() {
            if (this.selectedIndex < 0) return null
            const el = this.elements[this.selectedIndex]
            if (!el || !this.isScalable) return null
            if (this.activeTool !== 'select') return null
            const dist = 32
            const rad = (el.rotation || 0) * Math.PI / 180
            let cx, cy
            if (el.cx !== undefined) { cx = el.cx; cy = el.cy }
            else if (el.x1 !== undefined) { cx = (el.x1 + el.x2) / 2; cy = (el.y1 + el.y2) / 2 }
            else return null
            // Position below-right (135 degrees offset from rotation handle)
            const angle = rad + Math.PI * 0.75
            return {
                lx: cx, ly: cy,
                x: cx + dist * Math.sin(angle),
                y: cy - dist * Math.cos(angle),
            }
        },
        pitchBg() {
            if (this.currentBgSvg) return 'transparent'
            if (this.localBackground === 'blank' || this.localBackground === 'image') return '#1e293b'
            return '#2d8a3e'
        },
        lineC() {
            return 'rgba(255,255,255,0.5)'
        },
        // Background image computed position/size
        imgX() {
            return this.localImageTransform.x
        },
        imgY() {
            return this.localImageTransform.y
        },
        imgW() {
            return this.cW * this.localImageTransform.scale
        },
        imgH() {
            return this.cH * this.localImageTransform.scale
        },
    },
    watch: {
        savedElements: {
            handler(val) {
                if (JSON.stringify(val) !== JSON.stringify(this.elements)) {
                    this.elements = val ? JSON.parse(JSON.stringify(val)) : []
                }
            },
            immediate: true,
        },
        title(val) {
            this.localTitle = val
        },
        notes(val) {
            this.localNotes = val
        },
        background(val) {
            this.localBackground = val
        },
        savedBackgroundImage(val) {
            this.localBackgroundImage = val
        },
        orientation(val) {
            this.localOrientation = val || 'portrait'
        },
        imageTransform: {
            handler(val) {
                if (val) this.localImageTransform = { x: 0, y: 0, scale: 1, ...val }
            },
            deep: true,
        },
    },
    mounted() {
        document.addEventListener('keydown', this.handleKeydown)
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.handleKeydown)
        document.removeEventListener('mousemove', this.onBgImageMove)
        document.removeEventListener('mouseup', this.onBgImageUp)
        document.removeEventListener('touchmove', this.onBgImageMove)
        document.removeEventListener('touchend', this.onBgImageUp)
    },
    methods: {
        // --- Coordinate helpers ---
        getSvgPoint(e) {
            const svg = this.$refs.svgCanvas
            if (!svg) return { x: 0, y: 0 }
            const pt = svg.createSVGPoint()
            const touch = e.touches ? e.touches[0] || e.changedTouches[0] : e
            pt.x = touch.clientX
            pt.y = touch.clientY
            const ctm = svg.getScreenCTM().inverse()
            const svgPt = pt.matrixTransform(ctm)
            return { x: Math.round(svgPt.x), y: Math.round(svgPt.y) }
        },

        // --- Pointer handlers ---
        onPointerDown(e) {
            if (!this.editMode) return
            const pt = this.getSvgPoint(e)

            if (this.activeTool === 'select') {
                // Deselect if clicking canvas directly
                this.selectedIndex = -1
                return
            }

            // Instant placement tools
            if (this.activeTool === 'player') {
                this.elements.push({
                    type: 'player',
                    cx: pt.x,
                    cy: pt.y,
                    r: 16,
                    color: this.activeColor,
                    strokeWidth: this.activeWidth,
                    label: String(this.activePlayerNumber),
                    rotation: 0,
                    scale: 1,
                })
                this.activePlayerNumber = Math.min(99, this.activePlayerNumber + 1)
                this.emitElements()
                return
            }

            if (this.activeTool === 'text') {
                const text = prompt('Enter text label:')
                if (!text) return
                this.elements.push({
                    type: 'text',
                    cx: pt.x,
                    cy: pt.y,
                    text,
                    color: this.activeColor,
                    fontSize: this.activeFontSize,
                    rotation: 0,
                })
                this.emitElements()
                return
            }

            if (this.activeTool.startsWith('goal-')) {
                const sizeMap = { 'goal-small': 'small', 'goal-medium': 'medium', 'goal-large': 'large' }
                this.elements.push({
                    type: 'goal',
                    cx: pt.x,
                    cy: pt.y,
                    color: this.activeColor,
                    strokeWidth: this.activeWidth,
                    goalSize: sizeMap[this.activeTool] || 'large',
                    rotation: 0,
                    scale: 1,
                })
                this.emitElements()
                return
            }

            if (this.activeTool === 'ball') {
                this.elements.push({
                    type: 'ball',
                    cx: pt.x,
                    cy: pt.y,
                    color: this.activeColor,
                    rotation: 0,
                    scale: 1,
                })
                this.emitElements()
                return
            }

            if (this.activeTool === 'cone') {
                this.elements.push({
                    type: 'cone',
                    cx: pt.x,
                    cy: pt.y,
                    color: this.activeColor,
                    rotation: 0,
                    scale: 1,
                })
                this.emitElements()
                return
            }

            if (this.activeTool === 'ladder') {
                this.elements.push({
                    type: 'ladder',
                    cx: pt.x,
                    cy: pt.y,
                    color: this.activeColor,
                    rotation: 0,
                    scale: 1,
                })
                this.emitElements()
                return
            }

            if (this.activeTool === 'manikin') {
                this.elements.push({
                    type: 'manikin',
                    cx: pt.x,
                    cy: pt.y,
                    color: this.activeColor,
                    rotation: 0,
                    scale: 1,
                })
                this.emitElements()
                return
            }

            if (this.activeTool === 'hurdle') {
                this.elements.push({
                    type: 'hurdle',
                    cx: pt.x,
                    cy: pt.y,
                    color: this.activeColor,
                    rotation: 0,
                    scale: 1,
                })
                this.emitElements()
                return
            }

            if (this.activeTool === 'disk') {
                this.elements.push({
                    type: 'disk',
                    cx: pt.x,
                    cy: pt.y,
                    color: this.activeColor,
                    rotation: 0,
                    scale: 1,
                })
                this.emitElements()
                return
            }

            // Freehand
            if (this.activeTool === 'freehand') {
                this.drawing = true
                this.freehandPoints = [pt]
                return
            }

            // Drag-to-draw tools
            this.drawing = true
            this.drawStart = pt
            this.previewEl = this.buildPreview(pt, pt)
        },

        onPointerMove(e) {
            if (!this.editMode) return
            const pt = this.getSvgPoint(e)

            // Rotating an element
            if (this.rotating && this.selectedIndex >= 0) {
                const el = this.elements[this.selectedIndex]
                if (el) {
                    let cx, cy
                    if (el.cx !== undefined) { cx = el.cx; cy = el.cy }
                    else if (el.x1 !== undefined) { cx = (el.x1 + el.x2) / 2; cy = (el.y1 + el.y2) / 2 }
                    if (cx !== undefined) {
                        const dx = pt.x - cx
                        const dy = pt.y - cy
                        let angle = Math.atan2(dx, -dy) * 180 / Math.PI
                        if (e.shiftKey) angle = Math.round(angle / 15) * 15
                        el.rotation = Math.round(angle)
                    }
                }
                return
            }

            // Resizing an element
            if (this.resizing && this.selectedIndex >= 0) {
                const el = this.elements[this.selectedIndex]
                if (el) {
                    let cx, cy
                    if (el.cx !== undefined) { cx = el.cx; cy = el.cy }
                    else if (el.x1 !== undefined) { cx = (el.x1 + el.x2) / 2; cy = (el.y1 + el.y2) / 2 }
                    if (cx !== undefined) {
                        const dist = Math.sqrt((pt.x - cx) ** 2 + (pt.y - cy) ** 2) || 1
                        let newScale = this.scaleStartVal * (dist / this.scaleStartDist)
                        newScale = Math.max(0.3, Math.min(4, newScale))
                        newScale = Math.round(newScale * 100) / 100
                        el.scale = newScale
                    }
                }
                return
            }

            if (this.dragging && this.selectedIndex >= 0) {
                const el = this.elements[this.selectedIndex]
                if (!el) return
                this.moveElement(el, pt)
                return
            }

            // Freehand
            if (this.drawing && this.activeTool === 'freehand') {
                this.freehandPoints.push(pt)
                return
            }

            if (this.drawing && this.drawStart) {
                this.previewEl = this.buildPreview(this.drawStart, pt)
            }
        },

        onPointerUp(e) {
            if (this.rotating) {
                this.rotating = false
                this.emitElements()
                return
            }

            if (this.resizing) {
                this.resizing = false
                this.emitElements()
                return
            }

            if (this.dragging) {
                this.dragging = false
                this.emitElements()
                return
            }

            // End freehand
            if (this.drawing && this.activeTool === 'freehand' && this.freehandPoints.length > 2) {
                const pts = this.freehandPoints
                let d = `M ${pts[0].x} ${pts[0].y}`
                for (let i = 1; i < pts.length; i++) {
                    d += ` L ${pts[i].x} ${pts[i].y}`
                }
                this.elements.push({
                    type: 'freehand',
                    d,
                    color: this.activeColor,
                    strokeWidth: this.activeWidth,
                })
                this.freehandPoints = []
                this.drawing = false
                this.emitElements()
                return
            }

            if (!this.drawing || !this.drawStart) return

            const pt = this.getSvgPoint(e)
            const dx = Math.abs(pt.x - this.drawStart.x)
            const dy = Math.abs(pt.y - this.drawStart.y)

            // Minimum drag distance to create element
            if (dx > 5 || dy > 5) {
                const el = this.buildElement(this.drawStart, pt)
                if (el) {
                    this.elements.push(el)
                    this.emitElements()
                }
            }

            this.drawing = false
            this.drawStart = null
            this.previewEl = null
        },

        // --- Element interaction ---
        onElementDown(idx, e) {
            if (!this.editMode) return
            e.stopPropagation()
            this.selectedIndex = idx

            if (this.activeTool === 'select') {
                const pt = this.getSvgPoint(e)
                const el = this.elements[idx]
                // Calculate offset so drag is smooth
                if (el.cx !== undefined) {
                    this.dragOffset = { dx: el.cx - pt.x, dy: el.cy - pt.y }
                } else if (el.x1 !== undefined) {
                    this.dragOffset = { dx: el.x1 - pt.x, dy: el.y1 - pt.y }
                } else if (el.type === 'freehand' && el.d) {
                    this.dragOffset = { dx: -pt.x, dy: -pt.y }
                    this._freehandLastDx = 0
                    this._freehandLastDy = 0
                }
                this.dragging = true
            }
        },

        moveElement(el, pt) {
            if (el.cx !== undefined && el.cy !== undefined) {
                // Point-based elements (circle, player, text)
                el.cx = pt.x + this.dragOffset.dx
                el.cy = pt.y + this.dragOffset.dy
            } else if (el.x1 !== undefined) {
                // Line-based elements
                const w = el.x2 - el.x1
                const h = el.y2 - el.y1
                const oldX1 = el.x1
                const oldY1 = el.y1
                el.x1 = pt.x + this.dragOffset.dx
                el.y1 = pt.y + this.dragOffset.dy
                el.x2 = el.x1 + w
                el.y2 = el.y1 + h
                // Move control point for curved arrows
                if (el.cpx !== undefined) {
                    el.cpx += el.x1 - oldX1
                    el.cpy += el.y1 - oldY1
                }
            } else if (el.type === 'freehand' && el.d) {
                const moveDx = pt.x + this.dragOffset.dx - (this._freehandLastDx || 0)
                const moveDy = pt.y + this.dragOffset.dy - (this._freehandLastDy || 0)
                this._freehandLastDx = pt.x + this.dragOffset.dx
                this._freehandLastDy = pt.y + this.dragOffset.dy
                el.d = el.d.replace(/(\d+\.?\d*)\s+(\d+\.?\d*)/g, (match, x, y) => {
                    return `${(parseFloat(x) + moveDx).toFixed(1)} ${(parseFloat(y) + moveDy).toFixed(1)}`
                })
            }
        },

        // --- Build element objects ---
        buildPreview(from, to) {
            return this.buildElement(from, to)
        },

        buildElement(from, to) {
            const tool = this.activeTool
            const base = { color: this.activeColor, strokeWidth: this.activeWidth, rotation: 0 }

            switch (tool) {
                case 'arrow':
                    return { ...base, type: 'arrow', x1: from.x, y1: from.y, x2: to.x, y2: to.y }
                case 'curved-arrow':
                case 'curved-arrow-right': {
                    const mx = (from.x + to.x) / 2
                    const my = (from.y + to.y) / 2
                    const ddx = to.x - from.x
                    const ddy = to.y - from.y
                    const len = Math.sqrt(ddx * ddx + ddy * ddy) || 1
                    const offset = Math.min(len * 0.4, 60)
                    const dir = tool === 'curved-arrow' ? 1 : -1
                    const cpx = mx + (-ddy / len) * offset * dir
                    const cpy = my + (ddx / len) * offset * dir
                    return { ...base, type: 'curved-arrow', x1: from.x, y1: from.y, x2: to.x, y2: to.y, cpx, cpy }
                }
                case 'line':
                    return { ...base, type: 'line', x1: from.x, y1: from.y, x2: to.x, y2: to.y }
                case 'dashed':
                    return { ...base, type: 'dashed', x1: from.x, y1: from.y, x2: to.x, y2: to.y }
                case 'circle-filled': {
                    const r = Math.sqrt((to.x - from.x) ** 2 + (to.y - from.y) ** 2)
                    return { ...base, type: 'circle-filled', cx: from.x, cy: from.y, r: Math.max(5, r) }
                }
                case 'circle-empty': {
                    const r = Math.sqrt((to.x - from.x) ** 2 + (to.y - from.y) ** 2)
                    return { ...base, type: 'circle-empty', cx: from.x, cy: from.y, r: Math.max(5, r) }
                }
                case 'zone':
                    return { ...base, type: 'zone', x1: from.x, y1: from.y, x2: to.x, y2: to.y }
                default:
                    return null
            }
        },

        // --- Rotation ---
        onRotateStart(e) {
            if (this.selectedIndex < 0) return
            const el = this.elements[this.selectedIndex]
            if (!el || !this.isRotatable) return
            e.stopPropagation()
            this.rotating = true
        },

        onScaleStart(e) {
            if (this.selectedIndex < 0) return
            const el = this.elements[this.selectedIndex]
            if (!el || !this.isScalable) return
            e.stopPropagation()
            const pt = this.getSvgPoint(e)
            let cx, cy
            if (el.cx !== undefined) { cx = el.cx; cy = el.cy }
            else if (el.x1 !== undefined) { cx = (el.x1 + el.x2) / 2; cy = (el.y1 + el.y2) / 2 }
            else return
            this.scaleStartDist = Math.sqrt((pt.x - cx) ** 2 + (pt.y - cy) ** 2) || 1
            this.scaleStartVal = el.scale || 1
            this.resizing = true
        },

        rotateSelected(deg) {
            if (this.selectedIndex < 0) return
            const el = this.elements[this.selectedIndex]
            if (!el || !this.isRotatable) return
            el.rotation = ((el.rotation || 0) + deg + 360) % 360
            this.emitElements()
        },

        scaleSelected(delta) {
            if (this.selectedIndex < 0) return
            const el = this.elements[this.selectedIndex]
            if (!el || !this.isScalable) return
            let newScale = (el.scale || 1) + delta
            newScale = Math.max(0.3, Math.min(4, newScale))
            el.scale = Math.round(newScale * 100) / 100
            this.emitElements()
        },

        elementTransform(el) {
            const rot = el.rotation || 0
            const s = el.scale || 1
            let cx, cy
            if (el.cx !== undefined) { cx = el.cx; cy = el.cy }
            else if (el.x1 !== undefined) { cx = (el.x1 + el.x2) / 2; cy = (el.y1 + el.y2) / 2 }
            else return ''
            if (s === 1 && rot === 0) return ''
            if (s === 1) return `rotate(${rot}, ${cx}, ${cy})`
            // Scale around center, then rotate around center
            return `translate(${cx}, ${cy}) scale(${s}) rotate(${rot}) translate(${-cx}, ${-cy})`
        },

        // --- Actions ---
        undo() {
            if (this.elements.length === 0) return
            this.elements.pop()
            this.selectedIndex = -1
            this.emitElements()
        },

        clearAll() {
            if (this.elements.length === 0) return
            if (!confirm('Clear all drawings?')) return
            this.elements = []
            this.selectedIndex = -1
            this.emitElements()
        },

        deleteSelected() {
            if (this.selectedIndex < 0) return
            this.elements.splice(this.selectedIndex, 1)
            this.selectedIndex = -1
            this.emitElements()
        },

        // --- Orientation ---
        setOrientation(o) {
            this.localOrientation = o
            this.$emit('update:orientation', o)
        },

        // --- Background ---
        setBackground(id) {
            this.localBackground = id
            if (id !== 'image') this.localBackgroundImage = ''
            this.$emit('update:background', id)
        },

        // --- Active state helper for background buttons ---
        isBgActive(id) {
            return this.localBackground === id
        },

        handleImageUpload(e) {
            const file = e.target.files?.[0]
            if (!file) return
            if (file.size > 2 * 1024 * 1024) {
                alert('Image must be under 2 MB')
                return
            }
            const reader = new FileReader()
            reader.onload = () => {
                this.localBackgroundImage = reader.result
                this.localBackground = 'image'
                this.localImageTransform = { x: 0, y: 0, scale: 1 }
                this.$emit('update:background', 'image')
                this.$emit('update:backgroundImage', reader.result)
                this.$emit('update:imageTransform', { ...this.localImageTransform })
            }
            reader.readAsDataURL(file)
        },

        // --- Background image drag & scale ---
        onBgImageDown(e) {
            if (!this.editMode || this.activeTool !== 'select') return
            const pt = this.getSvgPoint(e)
            this.draggingImage = true
            this.imgDragStart = { x: pt.x - this.localImageTransform.x, y: pt.y - this.localImageTransform.y }
            document.addEventListener('mousemove', this.onBgImageMove)
            document.addEventListener('mouseup', this.onBgImageUp)
            document.addEventListener('touchmove', this.onBgImageMove, { passive: false })
            document.addEventListener('touchend', this.onBgImageUp)
        },

        onBgImageMove(e) {
            if (!this.draggingImage) return
            e.preventDefault()
            const pt = this.getSvgPoint(e)
            this.localImageTransform.x = pt.x - this.imgDragStart.x
            this.localImageTransform.y = pt.y - this.imgDragStart.y
        },

        onBgImageUp() {
            if (!this.draggingImage) return
            this.draggingImage = false
            this.imgDragStart = null
            document.removeEventListener('mousemove', this.onBgImageMove)
            document.removeEventListener('mouseup', this.onBgImageUp)
            document.removeEventListener('touchmove', this.onBgImageMove)
            document.removeEventListener('touchend', this.onBgImageUp)
            this.$emit('update:imageTransform', { ...this.localImageTransform })
        },

        adjustImageScale(delta) {
            const newScale = Math.max(0.2, Math.min(5, this.localImageTransform.scale + delta))
            this.localImageTransform.scale = Math.round(newScale * 100) / 100
            this.$emit('update:imageTransform', { ...this.localImageTransform })
        },

        resetImageTransform() {
            this.localImageTransform = { x: 0, y: 0, scale: 1 }
            this.$emit('update:imageTransform', { ...this.localImageTransform })
        },

        // --- Keyboard ---
        handleKeydown(e) {
            if (!this.editMode) return

            // Don't intercept keys when typing in inputs/textareas
            const tag = e.target.tagName
            if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return

            if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedIndex >= 0) {
                e.preventDefault()
                this.deleteSelected()
            }
            if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault()
                this.undo()
            }
            if ((e.key === 'r' || e.key === 'R') && this.selectedIndex >= 0) {
                e.preventDefault()
                this.rotateSelected(e.shiftKey ? -15 : 15)
            }
            if ((e.key === '+' || e.key === '=') && this.selectedIndex >= 0 && this.isScalable) {
                e.preventDefault()
                this.scaleSelected(0.1)
            }
            if ((e.key === '-' || e.key === '_') && this.selectedIndex >= 0 && this.isScalable) {
                e.preventDefault()
                this.scaleSelected(-0.1)
            }
            if (e.key === 'Escape') {
                this.selectedIndex = -1
            }
        },

        // --- Emitters ---
        emitElements() {
            this.$emit('update:elements', JSON.parse(JSON.stringify(this.elements)))
        },

        onTitleBlur(e) {
            this.localTitle = e.target.textContent?.trim() || 'Tactical Board'
            this.$emit('update:title', this.localTitle)
        },

        emitNotes() {
            this.$emit('update:notes', this.localNotes)
        },
    },
}
</script>

<style scoped>
.tactical-board-slide {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    color: #e2e8f0;
    padding: 28px 32px;
    display: flex;
    flex-direction: column;
    font-family: 'Inter', system-ui, sans-serif;
    border-radius: 12px;
    min-height: 380px;
}

/* ---- Header ---- */
.slide-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    flex-shrink: 0;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.slide-header h2 {
    font-size: 1.35rem;
    font-weight: 700;
    color: #f1f5f9;
    margin: 0;
}

.editable-title {
    outline: none;
    border-bottom: 2px dashed rgba(255, 255, 255, 0.25);
    cursor: text;
    min-width: 120px;
    padding: 2px 4px;
}

.editable-title:focus {
    border-bottom-color: #667eea;
}

/* ---- Header controls ---- */
.header-controls {
    display: flex;
    gap: 12px;
    align-items: center;
}

/* ---- Background selector ---- */
.background-selector {
    display: flex;
    gap: 4px;
}

.bg-btn {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #94a3b8;
    border-radius: 6px;
    padding: 5px 8px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.15s;
}

.bg-btn:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #e2e8f0;
}

.bg-btn.active {
    background: rgba(102, 126, 234, 0.25);
    border-color: #667eea;
    color: #667eea;
}

.upload-btn {
    position: relative;
}

.upload-btn input[type="file"] {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
}

/* ---- Board content ---- */
.board-content {
    display: flex;
    gap: 16px;
    position: relative;
}

/* ---- Toolbar ---- */
.toolbar {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 56px;
    flex-shrink: 0;
    padding: 8px 4px;
    background: rgba(255, 255, 255, 0.04);
    border-radius: 10px;
    align-items: center;
    overflow-y: auto;
    position: relative;
    z-index: 2;
}

.tool-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.group-label {
    font-size: 0.55rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
    font-weight: 600;
}

.tool-buttons {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.tool-btn {
    width: 36px;
    height: 32px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    transition: all 0.15s;
}

.tool-btn:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #e2e8f0;
}

.tool-btn.active {
    background: rgba(102, 126, 234, 0.2);
    border-color: #667eea;
    color: #667eea;
}

/* ---- Color palette ---- */
.color-palette {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 3px;
}

.color-palette button {
    padding: 0;
}

.color-swatch {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid transparent;
    cursor: pointer;
    transition: transform 0.1s;
}

.color-swatch:hover {
    transform: scale(1.2);
}

.color-swatch.active {
    border-color: #fff;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.5);
}

/* ---- Width options ---- */
.width-options {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.width-btn {
    width: 36px;
    height: 20px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
}

.width-btn:hover {
    background: rgba(255, 255, 255, 0.08);
}

.width-btn.active {
    background: rgba(102, 126, 234, 0.2);
    border-color: #667eea;
}

.width-line {
    width: 24px;
    background: #94a3b8;
    border-radius: 2px;
}

/* ---- Font select ---- */
.font-select {
    width: 44px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #e2e8f0;
    border-radius: 4px;
    padding: 2px;
    font-size: 0.7rem;
}

.font-select option {
    background: #1e293b;
}

/* ---- Player number input ---- */
.player-number-input {
    width: 44px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #e2e8f0;
    border-radius: 4px;
    padding: 2px 4px;
    font-size: 0.75rem;
    text-align: center;
}

/* ---- Action buttons ---- */
.actions {
    margin-top: auto;
}

.action-btn {
    width: 36px;
    height: 32px;
    border: none;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.06);
    color: #94a3b8;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    transition: all 0.15s;
}

.action-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
    color: #e2e8f0;
}

.action-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.action-btn.danger:hover:not(:disabled) {
    background: rgba(244, 63, 94, 0.2);
    color: #f43f5e;
}

/* ---- Canvas ---- */
/* Canvas + notes share a column beside the toolbar so the notes panel
   sits BELOW the pitch instead of overlapping it. */
.board-stage {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.canvas-wrap {
    width: 100%;
    min-width: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
}

.drawing-canvas {
    max-height: 65vh;
    width: auto;
    border-radius: 10px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
    overflow: hidden;
    display: block;
}

.drawing-canvas.editing {
    cursor: crosshair;
}

.drawing-canvas.cursor-select {
    cursor: default;
}

.drawing-canvas.cursor-text {
    cursor: text;
}

.drawing-canvas.cursor-player {
    cursor: copy;
}

.drawing-canvas.landscape {
    aspect-ratio: 7 / 5;
}

.bg-image.draggable {
    cursor: grab;
}

.bg-image.draggable:active {
    cursor: grabbing;
}

/* ---- Drawn elements ---- */
.drawn-element {
    cursor: default;
}

.editing .drawn-element {
    cursor: pointer;
}

.drawn-element.selected {
    filter: drop-shadow(0 0 4px rgba(102, 126, 234, 0.8));
}

/* Rotation handle */
.rotation-handle {
    pointer-events: none;
}

.rotation-handle circle {
    pointer-events: all;
    transition: fill 0.15s;
}

.rotation-handle circle:hover {
    fill: #8b9cf7;
}

.scale-handle {
    pointer-events: none;
}

.scale-handle circle {
    pointer-events: all;
    transition: fill 0.15s;
}

.scale-handle circle:hover {
    fill: #4ade80;
}

/* ---- Notes panel ---- */
.notes-panel {
    width: 100%;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
    max-width: 40rem;
}

.notes-panel h4 {
    font-size: 0.8rem;
    font-weight: 600;
    color: #94a3b8;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
}

/* Quill editor dark theme overrides */
.notes-editor-wrapper {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.notes-editor-wrapper :deep(.ql-toolbar) {
    background: rgba(255, 255, 255, 0.06);
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding: 6px 8px;
}

.notes-editor-wrapper :deep(.ql-toolbar .ql-stroke) {
    stroke: #94a3b8;
}

.notes-editor-wrapper :deep(.ql-toolbar .ql-fill) {
    fill: #94a3b8;
}

.notes-editor-wrapper :deep(.ql-toolbar .ql-picker-label) {
    color: #94a3b8;
}

.notes-editor-wrapper :deep(.ql-toolbar button:hover .ql-stroke),
.notes-editor-wrapper :deep(.ql-toolbar button.ql-active .ql-stroke) {
    stroke: #667eea;
}

.notes-editor-wrapper :deep(.ql-toolbar button:hover .ql-fill),
.notes-editor-wrapper :deep(.ql-toolbar button.ql-active .ql-fill) {
    fill: #667eea;
}

.notes-editor-wrapper :deep(.ql-toolbar .ql-picker-label:hover),
.notes-editor-wrapper :deep(.ql-toolbar .ql-picker-label.ql-active) {
    color: #667eea;
}

.notes-editor-wrapper :deep(.ql-toolbar .ql-picker-options) {
    background: #1e293b;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 6px;
}

.notes-editor-wrapper :deep(.ql-toolbar .ql-picker-item:hover),
.notes-editor-wrapper :deep(.ql-toolbar .ql-picker-item.ql-selected) {
    color: #667eea;
}

.notes-editor-wrapper :deep(.ql-container) {
    background: rgba(255, 255, 255, 0.04);
    border: none;
    font-family: inherit;
    font-size: 0.8rem;
    min-height: 100px;
}

.notes-editor-wrapper :deep(.ql-editor) {
    color: #434952;
    line-height: 1.6;
    padding: 10px;
    min-height: 100px;
}

.notes-editor-wrapper :deep(.ql-editor.ql-blank::before) {
    color: #475569;
    font-style: italic;
}

.notes-editor-wrapper :deep(.ql-editor h3) {
    color: #f1f5f9;
    font-size: 1rem;
    margin-bottom: 6px;
}

.notes-editor-wrapper :deep(.ql-editor h4) {
    color: #e2e8f0;
    font-size: 0.9rem;
    margin-bottom: 4px;
}

.notes-editor-wrapper :deep(.ql-editor ul),
.notes-editor-wrapper :deep(.ql-editor ol) {
    padding-left: 1.2em;
}

.notes-display {
    flex: 1;
    padding: 10px;
    font-size: 0.8rem;
    line-height: 1.6;
    color: #cbd5e1;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
}

.notes-display .notes-rendered :deep(h3) {
    color: #f1f5f9;
    font-size: 1rem;
    margin: 0 0 6px;
}

.notes-display .notes-rendered :deep(h4) {
    color: #e2e8f0;
    font-size: 0.9rem;
    margin: 0 0 4px;
}

.notes-display .notes-rendered :deep(ul),
.notes-display .notes-rendered :deep(ol) {
    padding-left: 1.2em;
    margin: 4px 0;
}

.notes-display .notes-rendered :deep(strong) {
    color: #f1f5f9;
    font-weight: 600;
}

.notes-display .notes-rendered :deep(p) {
    margin: 0 0 4px;
}

.notes-display .placeholder {
    color: #475569;
    font-style: italic;
    background-color: transparent;
    cursor: default;
}
</style>
